import React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ExternalLink } from 'lucide-react';
import { capitalize } from '../../lib/utils';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { MonthCalendar } from '../../services/timesheetService';
import { PersonMonthRow } from './approvals.utils';
import {
  MonthDecisionBar,
  MonthLedger,
  MonthStatusPill,
  MonthSummary,
  useDetailLocale,
  usePendingSelection,
} from './TimesheetMonthDetail';

interface TimesheetMonthDialogProps {
  row: PersonMonthRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  year: number;
  month: number;
  relationshipLabel: string;
  companyName?: string;
  /** Working days and expected hours of the month; the summary reads the person's hours against them. */
  calendar?: MonthCalendar | null;
  processing: boolean;
  onApprove: (ids: number[]) => void;
  onReject: (ids: number[]) => void;
  /** Opens the person's full-page month (`/approvals/:userId`). */
  onOpenFullTimesheet: (userId: number) => void;
}

/** Quick review from the list: the person's month in a popup, decisions included. */
export function TimesheetMonthDialog({
  row,
  open,
  onOpenChange,
  year,
  month,
  relationshipLabel,
  companyName,
  calendar,
  processing,
  onApprove,
  onReject,
  onOpenFullTimesheet,
}: TimesheetMonthDialogProps) {
  const { t } = useTranslation();
  const { locale } = useDetailLocale();
  const selection = usePendingSelection(row, open);

  if (!row) return null;

  const monthLabel = capitalize(format(new Date(year, month - 1, 1), 'LLLL yyyy', { locale }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-3xl">
        <DialogHeader className="space-y-3 border-b px-5 pb-4 pt-5 pr-12 text-left sm:px-6">
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div className="min-w-0">
              <DialogTitle className="text-xl font-semibold tracking-tight">{row.name}</DialogTitle>
              <DialogDescription className="mt-1">
                {[relationshipLabel, companyName, monthLabel].filter(Boolean).join(' · ')}
              </DialogDescription>
            </div>
            <MonthStatusPill pendingCount={row.pendingIds.length} />
          </div>
          <MonthSummary row={row} calendar={calendar} />
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2 sm:px-3">
          <MonthLedger row={row} selection={selection} />
        </div>

        <MonthDecisionBar
          className="border-t px-5 py-3 sm:px-6"
          pendingCount={row.pendingIds.length}
          selectedCount={selection.selected.size}
          processing={processing}
          onApprove={() => onApprove(Array.from(selection.selected))}
          onReject={() => onReject(Array.from(selection.selected))}
          leading={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-2 text-muted-foreground"
              onClick={() => onOpenFullTimesheet(row.userId)}
            >
              <ExternalLink className="h-4 w-4" />
              {t('approvals.detail.openFull', 'Apri timesheet completo')}
            </Button>
          }
        />
      </DialogContent>
    </Dialog>
  );
}

export default TimesheetMonthDialog;
